"use client"

import {
  useState,
  useEffect,
  useId,
  useMemo,
  useDeferredValue,
  useRef,
  Suspense,
} from "react"
import Image from "next/image"
import Link from "next/link"
import { m, AnimatePresence } from "framer-motion"
import { ClientOnly } from "@/lib/animation/client-only"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { buttonVariants } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { faqKeys as k } from "@/i18n/faq-keys"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { isAllowedInCountry } from "@/lib/country-gates"
import { useCountry } from "@/components/layout/country-provider"
import { useParams } from "next/navigation"
import { CloseIcon } from "@/components/ui/icons"
import { ChevronDownSmIcon } from "@/components/ui/icons"
import { POPULAR_SLUG } from "@/data/faq"
import { searchFaq, tokenize, type SearchableFaq } from "@/lib/faq-search"
import { Highlight } from "@/components/ui/highlight"
import { cmsLocale, type CmsFaqCategory, type FaqItem } from "@/lib/cms"
import { useEditRegion } from "@/lib/edit/edit-region-registry"
import { track } from "@/lib/analytics"
import { blobMediaUrl } from "@/lib/blob-url"
import { FaqUrlSync } from "./faq-url-sync"

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function stripHtml(html: string) {
  return html
    .replace(/<[^>]*>/g, "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .trim()
}

// ---------------------------------------------------------------------------
// Accordion item
// ---------------------------------------------------------------------------

export function AccordionItem({
  question,
  answer,
  slug,
  category,
  searchTokens,
  isOpen,
  onToggle,
  cmsId,
  editLocale,
}: {
  question: string
  answer: string
  slug?: string
  category?: string
  searchTokens?: string[]
  isOpen: boolean
  onToggle: () => void
  /** Payload doc id — registers the inline-editor regions. Absent → no region. */
  cmsId?: string
  /** CMS locale these docs were fetched in (region provenance). */
  editLocale: string
}) {
  const lp = useLocalePath()
  const t = useTranslations("faq")
  const panelId = useId()
  const questionRef = useEditRegion<HTMLSpanElement>(
    cmsId
      ? {
          collection: "faq",
          id: cmsId,
          field: "question",
          locale: editLocale,
          kind: "scalar",
        }
      : null
  )
  const buttonRef = useRef<HTMLButtonElement>(null)
  const wasOpen = useRef(isOpen)
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      // Defer one frame so the height transition has begun;
      // block: "nearest" keeps already-visible items stable.
      requestAnimationFrame(() => {
        buttonRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        })
      })
    }
    wasOpen.current = isOpen
  }, [isOpen])
  return (
    <div className={cn("border-b border-neutral-200 last:border-b-0")}>
      <button
        ref={buttonRef}
        type="button"
        onClick={onToggle}
        className="group flex w-full cursor-pointer items-start justify-between gap-4 py-5 text-left"
        aria-expanded={isOpen}
        aria-controls={panelId}
      >
        <span className="flex flex-col gap-1">
          {category && (
            <span className="type-label-sm text-neutral-400">{category}</span>
          )}
          <span
            ref={questionRef}
            className="group-hover:text-aubergine-900 type-body-lg font-medium text-neutral-900 transition-colors"
          >
            {searchTokens && searchTokens.length > 0 ? (
              <Highlight text={question} tokens={searchTokens} />
            ) : (
              question
            )}
          </span>
        </span>
        <span className="group-hover:text-aubergine-900 mt-0.5 text-neutral-400 transition-colors">
          <ChevronDownSmIcon
            width={18}
            height={18}
            className={cn(
              "flex-shrink-0 transition-transform duration-300",
              isOpen && "rotate-180"
            )}
          />
        </span>
      </button>

      {/* safe-animate-presence: conditional-mount — gated by `isOpen=true` */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <m.div
            key="answer"
            id={panelId}
            role="region"
            aria-live="polite"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="overflow-hidden"
          >
            {/*
              Answer HTML is sanitized + locale-prefixed server-side in page.tsx
              (sanitize-html in the client bundle triggers PPR bailout, AHD-5569).
            */}
            <FaqAnswer
              html={answer}
              cmsId={cmsId}
              editLocale={editLocale}
              className="[&_a]:text-aubergine-700 type-body-lg max-w-[70ch] pb-2 text-neutral-600 [&_a]:underline [&_a]:underline-offset-2 [&_h5]:mt-3 [&_h5]:mb-1 [&_h5]:font-semibold [&_li]:mb-1 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-3 last:[&_p]:mb-0 [&_strong]:font-semibold [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5"
            />
            {slug && (
              <Link
                href={lp(`/faq/${slug}`)}
                className="text-aubergine-700 hover:text-aubergine-900 inline-flex items-center gap-1 pb-5 text-xs font-medium transition-colors"
              >
                {t(k.openQuestionPage)}
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M6 4l4 4-4 4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
            )}
          </m.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// The answer is conditionally mounted (only when the item is open), so its edit
// region must register on expand / unregister on collapse. Extracting it into a
// child means `useEditRegion` runs on mount — exactly when the div appears.
function FaqAnswer({
  html,
  cmsId,
  editLocale,
  className,
}: {
  html: string
  cmsId?: string
  editLocale: string
  className: string
}) {
  const ref = useEditRegion<HTMLDivElement>(
    cmsId
      ? {
          collection: "faq",
          id: cmsId,
          field: "answer",
          locale: editLocale,
          kind: "html",
        }
      : null
  )
  return (
    <div
      ref={ref}
      className={className}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function FaqClient({
  initialCmsFaq,
  categories,
}: {
  initialCmsFaq: FaqItem[]
  /**
   * Categories for the sidebar — synthesized "most-popular" (built server-side
   * from the FAQ message namespace) followed by every active CMS row in
   * `order` ascending. Names + descriptions are pre-localized; the client
   * never needs to translate them.
   */
  categories: CmsFaqCategory[]
}) {
  const t = useTranslations("faq")
  const lp = useLocalePath()

  // Category slug → display name (pre-localized server-side)
  const categoryLabels: Record<string, string> = {}
  for (const cat of categories) {
    categoryLabels[cat.slug] = cat.name
  }

  const activeItems = initialCmsFaq.map((f) => ({
    id: f.id,
    slug: f.slug,
    categorySlug: f.category,
    category: categoryLabels[f.category] ?? f.category,
    question: f.question,
    answer: f.answer,
    featured: f.featured === true,
    updatedAt: f.updatedAt,
  }))

  // Sidebar tabs are slugs from the CMS list with "most-popular" prepended.
  // Most-popular is a virtual overlay filtered by `featured` rather than
  // `categorySlug` and only renders when at least one item is featured.
  const hasFeatured = activeItems.some((i) => i.featured)
  const CATEGORIES = categories
    .filter((c) => c.slug !== POPULAR_SLUG || hasFeatured)
    .map((c) => c.slug)

  const params = useParams<{ country: string; locale: string }>()
  const country = useCountry().code

  // CMS locale these docs were fetched in — the inline editor stamps it so a
  // draft write targets the matching Payload locale row. No-op in public prod.
  const editLocale = cmsLocale(useLocale(), country)

  const [activeCategory, setActiveCategory] = useState(CATEGORIES[0])
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  // `?q=` URL persistence is handled by <FaqUrlSync> below — it's the
  // only place that touches `useSearchParams`, and it's wrapped in a
  // <Suspense> boundary so the dynamic-bailout it triggers under Next
  // 16 cacheComponents stays scoped to that subtree (rendering null)
  // instead of climbing to the root layout's <Suspense fallback={null}>
  // and emptying <main>. See faq-url-sync.tsx for the full rationale.
  const [searchQuery, setSearchQuery] = useState("")
  const deferredSearch = useDeferredValue(searchQuery)
  // The category-swap AnimatePresence below is mount-gated via
  // <ClientOnly> so framer-motion's PresenceChild (Math.random() / useId
  // in render) never reaches the SSR path — without this the subtree is
  // streamed at request time (ppr-deferred). The ClientOnly fallback
  // ships the same panel inside a plain <div>, so the static shell
  // contains the full FAQ list. Subsequent category swaps animate
  // normally on the client.

  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "/") return
      const tag = (document.activeElement as HTMLElement | null)?.tagName
      if (tag === "INPUT" || tag === "TEXTAREA") return
      e.preventDefault()
      searchInputRef.current?.focus()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  const isSearching = deferredSearch.trim().length > 0

  const searchableItems = useMemo<SearchableFaq[]>(
    () =>
      activeItems.map((i) => ({
        slug: i.slug,
        category: i.category,
        question: i.question,
        answer: stripHtml(i.answer),
      })),
    [activeItems]
  )

  const searchResults = useMemo(
    () => (isSearching ? searchFaq(deferredSearch, searchableItems) : []),
    [deferredSearch, isSearching, searchableItems]
  )

  // For rendering: if searching, use ranked results joined back to
  // the original (un-stripped) HTML answer; otherwise filter by category.
  const visibleItems = useMemo(() => {
    if (isSearching) {
      const bySlug = new Map(activeItems.map((i) => [i.slug, i]))
      return searchResults
        .map((r) => bySlug.get(r.slug))
        .filter((x): x is (typeof activeItems)[number] => Boolean(x))
    }
    if (activeCategory === POPULAR_SLUG) {
      return activeItems.filter((item) => item.featured)
    }
    return activeItems.filter((item) => item.categorySlug === activeCategory)
  }, [activeItems, activeCategory, isSearching, searchResults])

  useEffect(() => {
    if (isSearching && visibleItems.length === 1) {
      setOpenIndex(0)
    }
  }, [isSearching, visibleItems.length])

  // PostHog `search_query` — debounced + dedup + min-length so the
  // FAQ scoped search shares a single event schema with the global search.
  const lastTrackedFaqRef = useRef<{ query: string; count: number } | null>(
    null
  )
  useEffect(() => {
    const q = deferredSearch.trim()
    if (q.length < 3) return
    const handle = setTimeout(() => {
      const last = lastTrackedFaqRef.current
      if (last && last.query === q && last.count === visibleItems.length) return
      lastTrackedFaqRef.current = { query: q, count: visibleItems.length }
      track.searchQuery({
        query: q,
        result_count: visibleItems.length,
        surface: "faq",
        locale: params?.locale ?? "unknown",
        zero_results: visibleItems.length === 0,
      })
    }, 1200)
    return () => clearTimeout(handle)
  }, [deferredSearch, visibleItems.length, params])

  function handleToggle(index: number) {
    setOpenIndex(openIndex === index ? null : index)
  }

  function handleCategoryChange(cat: string) {
    setActiveCategory(cat)
    setOpenIndex(null)
    setSearchQuery("")
  }

  function handleSearchChange(value: string) {
    setSearchQuery(value)
    setOpenIndex(null)
  }

  return (
    <>
      {/* `useSearchParams` is dynamic-only under Next 16 cacheComponents.
          Wrap the syncer in <Suspense> so its dynamic bailout is scoped
          here instead of bubbling to the root layout's empty fallback. */}
      <Suspense fallback={null}>
        <FaqUrlSync searchQuery={deferredSearch} onHydrate={setSearchQuery} />
      </Suspense>

      {/* Hero */}
      <section className="pt-12 pb-12 text-center md:pt-16">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <p className="type-label-lg text-neutral-400">{t(k.heroEyebrow)}</p>
          <h1 className="type-display-lg mx-auto mt-4 max-w-3xl text-balance text-neutral-900">
            {t(k.heroHeading)}
          </h1>
          <p className="type-body-lg mt-4 text-neutral-600">
            {t(k.heroSubtitlePrefix)}{" "}
            <Link
              href={lp("/contact")}
              className="text-aubergine-700 hover:text-aubergine-900 underline underline-offset-2 transition-colors"
            >
              {t(k.heroSubtitleLink)}
            </Link>
            .
          </p>

          {/* Search input */}
          <div className="relative mx-auto mt-8 max-w-md">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 text-neutral-400"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault()
                  handleSearchChange("")
                  searchInputRef.current?.blur()
                }
              }}
              placeholder={t("faq_searchPlaceholder")}
              className="focus:ring-aubergine-300 w-full rounded-full border border-neutral-200 bg-white py-3 pr-4 pl-11 text-sm text-neutral-900 transition-shadow placeholder:text-neutral-400 focus:ring-2 focus:outline-none"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => handleSearchChange("")}
                className="absolute top-1/2 right-4 -translate-y-1/2 cursor-pointer text-neutral-400 hover:text-neutral-600"
              >
                <CloseIcon width={16} height={16} />
              </button>
            )}
          </div>
          <p className="mt-2 text-center text-xs text-neutral-400">
            {t("faq_searchHint")}
          </p>
        </div>
      </section>

      {/* FAQ body */}
      <section className="pb-20">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div>
            <div className="flex flex-col gap-10 md:flex-row">
              {/* Sidebar — category nav (hidden during search) */}
              {!isSearching && (
                <nav className="flex-shrink-0 md:w-56">
                  <ul className="flex flex-wrap gap-2 md:flex-col">
                    {CATEGORIES.map((cat) => (
                      <li key={cat}>
                        <button
                          type="button"
                          onClick={() => handleCategoryChange(cat)}
                          className={cn(
                            "w-full cursor-pointer rounded-xl px-4 py-2.5 text-left text-sm font-medium transition-all",
                            activeCategory === cat
                              ? "bg-aubergine-900 text-white"
                              : "hover:text-aubergine-900 text-neutral-500 hover:bg-neutral-100"
                          )}
                        >
                          {categoryLabels[cat] ?? cat}
                        </button>
                      </li>
                    ))}
                  </ul>
                </nav>
              )}

              {/* Accordion panel — content renders directly on the server so
                  the full FAQ list is in the static HTML. AnimatePresence is
                  mount-gated via <ClientOnly> below, keeping Math.random()
                  / useId() out of the SSR path so this subtree isn't
                  marked as PPR. */}
              <div className="flex-1 rounded-[16px] bg-white px-6 md:px-8">
                {(() => {
                  const searchTokens = isSearching
                    ? tokenize(deferredSearch)
                    : []
                  const panel = (
                    <>
                      <p className="type-label-md flex items-center justify-between border-b border-neutral-200 py-5 text-neutral-400">
                        <span>
                          {isSearching
                            ? t("faq_allCategories")
                            : (categoryLabels[activeCategory] ??
                              activeCategory)}
                        </span>
                        {isSearching && (
                          <span aria-live="polite">
                            {visibleItems.length === 1
                              ? t("faq_resultsCountOne")
                              : `${visibleItems.length} ${t("faq_resultsLabel")}`}
                          </span>
                        )}
                      </p>
                      {visibleItems.length === 0 ? (
                        <p className="py-8 text-center text-sm text-neutral-400">
                          {t("faq_noResults")}
                        </p>
                      ) : (
                        visibleItems.map((item, index) => (
                          <AccordionItem
                            key={item.slug}
                            question={item.question}
                            answer={item.answer}
                            slug={item.slug}
                            category={isSearching ? item.category : undefined}
                            searchTokens={
                              isSearching ? searchTokens : undefined
                            }
                            isOpen={openIndex === index}
                            onToggle={() => handleToggle(index)}
                            cmsId={item.id}
                            editLocale={editLocale}
                          />
                        ))
                      )}
                    </>
                  )
                  return (
                    <ClientOnly fallback={<div>{panel}</div>}>
                      <AnimatePresence mode="wait">
                        <m.div
                          key={
                            isSearching
                              ? `search-${searchQuery}`
                              : activeCategory
                          }
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          transition={{ duration: 0.2, ease: "easeOut" }}
                        >
                          {panel}
                        </m.div>
                      </AnimatePresence>
                    </ClientOnly>
                  )
                })()}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="relative overflow-hidden py-20">
        <Image
          src={blobMediaUrl("/images/misc/advisory-bg.png")}
          alt=""
          fill
          className="absolute inset-0 object-cover"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-neutral-200/50 via-transparent to-neutral-200/50" />
        <div className="relative z-10 mx-auto max-w-[1440px] px-4 md:px-10">
          <div className="mx-auto max-w-lg">
            <div>
              <div className="rounded-[16px] bg-white/90 p-8 text-center shadow-lg backdrop-blur">
                <div className="flex justify-center -space-x-2">
                  <Image
                    src={blobMediaUrl("/images/avatars/ferdinand.png")}
                    alt=""
                    width={40}
                    height={40}
                    className="rounded-full border-2 border-white object-cover"
                  />
                  <Image
                    src={blobMediaUrl("/images/avatars/dennis.png")}
                    alt=""
                    width={40}
                    height={40}
                    className="rounded-full border-2 border-white object-cover"
                  />
                  <Image
                    src={blobMediaUrl("/images/avatars/laura.png")}
                    alt=""
                    width={40}
                    height={40}
                    className="rounded-full border-2 border-white object-cover"
                  />
                </div>
                <h3 className="type-headline-md mt-4 text-neutral-900">
                  {t(k.ctaHeading)}
                </h3>
                <p className="type-body-md mt-2 text-neutral-600">
                  {t(k.ctaText)}{" "}
                  <Link href={lp("/contact")} className="underline">
                    {t(k.ctaMessageLink)}
                  </Link>
                  .
                </p>
                <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
                  {/* Discovery-call page is gated out of NL during the waitlist phase. */}
                  {isAllowedInCountry("page:free-call", country) && (
                    <Link href={lp("/free-call")} className={buttonVariants()}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        className="mr-1"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <path d="M16 2v4M8 2v4M3 10h18" strokeLinecap="round" />
                      </svg>
                      {t(k.ctaBookButton)}
                    </Link>
                  )}
                  <Link
                    href={lp("/pricing")}
                    className={buttonVariants({ variant: "outline" })}
                  >
                    {t(k.ctaPricingButton)}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
